<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationBuyerProfilesAPI from 'dashboard/api/conversationBuyerProfiles';
import ConversationListingLinksAPI from 'dashboard/api/conversationListingLinks';
import PropertyDemandsAPI from 'dashboard/api/propertyDemands';
import LaisPropertySearch from 'dashboard/components-next/LaisPropertySearch.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const ACTIVE_STATES = ['suggested', 'confirmed'];
const DEMAND_LIFECYCLE_ACTIONS = [
  { key: 'pause', icon: 'i-lucide-pause-circle' },
  { key: 'resume', icon: 'i-lucide-play-circle' },
  { key: 'supersede', icon: 'i-lucide-git-branch-plus' },
  { key: 'recompute_matches', icon: 'i-lucide-refresh-cw' },
];

const links = ref([]);
const loading = ref(false);
const saving = ref(false);
const savingDemand = ref(false);
const savingDemandAction = ref('');
const error = ref('');
const demandContext = ref(null);
const editingDemand = ref(false);
const selectedProperty = ref(null);
const selectedSource = ref('');
const searchQuery = ref('');
const demandForm = ref({
  finalidade: '',
  budget_min: '',
  budget_max: '',
  regions: '',
  property_types: '',
  bedrooms: '',
  payment_kind: '',
  intent: '',
  first_purchase: false,
});

const normalizeLinks = data => data?.data || data?.payload || [];
const listingIdFor = property =>
  property?.primary_listing?.id ||
  property?.primaryListing?.id ||
  property?.imovel_listing?.id ||
  property?.imovelListing?.id ||
  property?.listing?.id ||
  property?.imovel_listing_id ||
  property?.imovelListingId ||
  property?.listing_id ||
  property?.listingId;

const activeLink = computed(
  () =>
    links.value.find(
      link => link.primary_context && ACTIVE_STATES.includes(link.state)
    ) ||
    links.value.find(link => ACTIVE_STATES.includes(link.state)) ||
    null
);

const historyLinks = computed(() =>
  links.value.filter(link => link.id !== activeLink.value?.id).slice(0, 4)
);

const selectedListingId = computed(() => listingIdFor(selectedProperty.value));
const canConfirmSelection = computed(
  () => !!selectedListingId.value && !saving.value
);
const activeDemand = computed(
  () => activeLink.value?.demand || demandContext.value
);
const activeActivity = computed(() => activeLink.value?.activity || null);
const activeDemandId = computed(
  () => activeDemand.value?.property_demand_id || null
);
const activeDemandActions = computed(() =>
  Array.isArray(activeDemand.value?.available_actions)
    ? activeDemand.value.available_actions
    : []
);
const demandLifecycleActions = computed(() =>
  DEMAND_LIFECYCLE_ACTIONS.filter(
    action =>
      activeDemandId.value && activeDemandActions.value.includes(action.key)
  )
);

const loadLinks = async () => {
  if (!props.conversationId) return;

  loading.value = true;
  error.value = '';

  try {
    const { data } = await ConversationListingLinksAPI.get(
      props.conversationId
    );
    demandContext.value = data?.meta?.demand || null;
    links.value = normalizeLinks(data);
  } catch {
    links.value = [];
    demandContext.value = null;
    error.value = t('CONVERSATION.LISTING_CONTEXT_CARD.LOAD_ERROR');
  } finally {
    loading.value = false;
  }
};

const selectedMissingListing = computed(
  () => !!selectedProperty.value && !selectedListingId.value
);

const titleFor = link =>
  link?.listing?.titulo ||
  link?.imovel?.titulo ||
  (link?.imovel_id
    ? t('CONVERSATION.LISTING_CONTEXT_CARD.FALLBACK_PROPERTY_NUMBER', {
        id: link.imovel_id,
      })
    : t('CONVERSATION.LISTING_CONTEXT_CARD.FALLBACK_UNTITLED_PROPERTY'));

const locationFor = link =>
  [link?.imovel?.bairro, link?.imovel?.cidade].filter(Boolean).join(', ');

const modalidadeFor = link => {
  if (link?.listing?.modalidade === 'venda') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.SALE');
  }
  if (link?.listing?.modalidade === 'aluguel') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.RENT');
  }
  if (link?.listing?.modalidade === 'temporada') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.SEASONAL');
  }
  return link?.listing?.modalidade || '';
};

const stateLabel = state => {
  if (state === 'suggested') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.STATES.SUGGESTED');
  }
  if (state === 'confirmed') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.STATES.CONFIRMED');
  }
  if (state === 'rejected') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.STATES.REJECTED');
  }
  if (state === 'superseded') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.STATES.SUPERSEDED');
  }
  return state || t('CONVERSATION.LISTING_CONTEXT_CARD.STATES.UNKNOWN');
};

const availabilityLabel = listing => {
  if (!listing) return '';
  return listing.available
    ? t('CONVERSATION.LISTING_CONTEXT_CARD.AVAILABILITY.AVAILABLE')
    : t('CONVERSATION.LISTING_CONTEXT_CARD.AVAILABILITY.UNAVAILABLE');
};

const versionLabel = listing =>
  t('CONVERSATION.LISTING_CONTEXT_CARD.VERSION_VALUE', {
    version: listing?.listing_version || 1,
  });

const formatMoney = value => {
  if (value === null || value === undefined || value === '') return '';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(numeric);
};

const demandFinalidadeLabel = finalidade => {
  if (finalidade === 'venda') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.SALE');
  }
  if (finalidade === 'aluguel') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.RENT');
  }
  if (finalidade === 'temporada') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.SEASONAL');
  }
  return finalidade || '';
};

const demandIntentLabel = intent => {
  if (intent === 'investor') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.INTENTS.INVESTOR');
  }
  if (intent === 'home') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.INTENTS.HOME');
  }
  return intent || '';
};

const paymentKindLabel = paymentKind => {
  if (paymentKind === 'a_vista') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.CASH');
  }
  if (paymentKind === 'financiamento') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.FINANCING');
  }
  if (paymentKind === 'a_vista_fgts') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.CASH_FGTS');
  }
  if (paymentKind === 'financiamento_fgts') {
    return t(
      'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.FINANCING_FGTS'
    );
  }
  return paymentKind || '';
};

const listText = values =>
  Array.isArray(values) ? values.filter(Boolean).join(', ') : '';

const budgetText = demand => {
  const min = formatMoney(demand?.budget_min);
  const max = formatMoney(demand?.budget_max);
  if (min && max) {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.BUDGET_RANGE', {
      min,
      max,
    });
  }
  return min || max || '';
};

const formatDateTime = value => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const textFromList = values =>
  Array.isArray(values) ? values.filter(Boolean).join(', ') : '';

const hydrateDemandForm = demand => {
  demandForm.value = {
    finalidade: demand?.finalidade || '',
    budget_min: demand?.budget_min || '',
    budget_max: demand?.budget_max || '',
    regions: textFromList(demand?.regions),
    property_types: textFromList(demand?.property_types),
    bedrooms: textFromList(demand?.bedrooms),
    payment_kind: demand?.payment_kind || '',
    intent: demand?.intent || '',
    first_purchase: Boolean(demand?.first_purchase),
  };
};

const startDemandEdit = () => {
  hydrateDemandForm(activeDemand.value);
  editingDemand.value = true;
};

const cancelDemandEdit = () => {
  hydrateDemandForm(activeDemand.value);
  editingDemand.value = false;
};

const splitText = value =>
  String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const splitIntegers = value =>
  splitText(value)
    .map(item => Number.parseInt(item, 10))
    .filter(Number.isFinite);

const numericOrNull = value => {
  if (value === '' || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const blankOrValue = value => value || null;

const demandPayload = () => ({
  buyer_profile: {
    finalidade: blankOrValue(demandForm.value.finalidade),
    budget_min: numericOrNull(demandForm.value.budget_min),
    budget_max: numericOrNull(demandForm.value.budget_max),
    regions: splitText(demandForm.value.regions),
    property_types: splitText(demandForm.value.property_types),
    bedrooms: splitIntegers(demandForm.value.bedrooms),
    payment_kind: blankOrValue(demandForm.value.payment_kind),
    intent: blankOrValue(demandForm.value.intent),
    first_purchase: demandForm.value.first_purchase,
  },
});

const demandRows = computed(() => {
  const demand = activeDemand.value;
  if (!demand) return [];

  return [
    {
      key: 'match_score',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.MATCH'),
      value:
        demand.match?.score !== undefined
          ? t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.MATCH_VALUE', {
              score: demand.match.score,
            })
          : '',
    },
    {
      key: 'finalidade',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.FINALIDADE'),
      value: demandFinalidadeLabel(demand.finalidade),
    },
    {
      key: 'budget',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.BUDGET'),
      value: budgetText(demand),
    },
    {
      key: 'regions',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.REGIONS'),
      value: listText(demand.regions),
    },
    {
      key: 'property_types',
      label: t(
        'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.PROPERTY_TYPES'
      ),
      value: listText(demand.property_types),
    },
    {
      key: 'bedrooms',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.BEDROOMS'),
      value: listText(demand.bedrooms),
    },
    {
      key: 'payment_kind',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.PAYMENT'),
      value: paymentKindLabel(demand.payment_kind),
    },
    {
      key: 'intent',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.INTENT'),
      value: demandIntentLabel(demand.intent),
    },
  ].filter(row => row.value);
});

const activityRows = computed(() => {
  const activity = activeActivity.value;
  if (!activity) return [];

  const visit = activity.visits?.next || activity.visits?.latest;
  const offer = activity.offers?.active || activity.offers?.latest;

  return [
    {
      key: 'visit',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.LABELS.VISIT'),
      value: visit
        ? [
            visit.status_label || visit.status,
            formatDateTime(visit.scheduled_at),
          ]
            .filter(Boolean)
            .join(' · ')
        : '',
    },
    {
      key: 'offer',
      label: t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.LABELS.OFFER'),
      value: offer ? offer.status_label || offer.status : '',
    },
  ].filter(row => row.value);
});

const taskLabel = key => {
  if (key === 'review_demand') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.TASKS.REVIEW_DEMAND');
  }
  if (key === 'schedule_visit') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.TASKS.SCHEDULE_VISIT');
  }
  if (key === 'create_offer') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.TASKS.CREATE_OFFER');
  }
  return key || '';
};

const taskStatusLabel = status => {
  if (status === 'done') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.TASK_STATUS.DONE');
  }
  if (status === 'open') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.TASK_STATUS.OPEN');
  }
  return status || '';
};

const taskRows = computed(() =>
  (activeActivity.value?.tasks || []).map(task => ({
    ...task,
    label: taskLabel(task.key),
    statusLabel: taskStatusLabel(task.status),
  }))
);

const demandActionLabel = action => {
  if (action === 'pause') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LIFECYCLE.PAUSE');
  }
  if (action === 'resume') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LIFECYCLE.RESUME');
  }
  if (action === 'supersede') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LIFECYCLE.SUPERSEDE');
  }
  if (action === 'recompute_matches') {
    return t(
      'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LIFECYCLE.RECOMPUTE_MATCHES'
    );
  }
  return action;
};

const demandActionSuccessAlert = action => {
  if (action === 'pause') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.PAUSE');
  }
  if (action === 'resume') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.RESUME');
  }
  if (action === 'supersede') {
    return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.SUPERSEDE');
  }
  if (action === 'recompute_matches') {
    return t(
      'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.RECOMPUTE_MATCHES'
    );
  }
  return t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.ACTION_ERROR');
};

const applyDemandProfile = profile => {
  demandContext.value = profile;
  links.value = links.value.map(link => ({ ...link, demand: profile }));
};

const updateFromResult = response => {
  const link = response?.data;
  if (!link?.id) return loadLinks();

  demandContext.value = link.demand || demandContext.value;
  links.value = [link, ...links.value.filter(item => item.id !== link.id)];
  return loadLinks();
};

const confirmSelection = async () => {
  if (!selectedProperty.value) {
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.ALERTS.SELECT_PROPERTY'));
    return;
  }

  if (!selectedListingId.value) {
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.ALERTS.MISSING_LISTING'));
    return;
  }

  saving.value = true;

  try {
    const response = await ConversationListingLinksAPI.create(
      props.conversationId,
      {
        imovel_listing_id: selectedListingId.value,
        state: 'confirmed',
        primary_context: true,
        source: 'manual',
        evidence: {
          reason: 'inbox_listing_context_card',
          ui_surface: 'conversation_sidebar',
        },
      }
    );
    await updateFromResult(response);
    selectedProperty.value = null;
    selectedSource.value = '';
    searchQuery.value = '';
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.ALERTS.CONFIRMED'));
  } catch {
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.ALERTS.CONFIRM_ERROR'));
  } finally {
    saving.value = false;
  }
};

const rejectActiveLink = async () => {
  if (!activeLink.value || saving.value) return;

  saving.value = true;

  try {
    const response = await ConversationListingLinksAPI.reject(
      props.conversationId,
      activeLink.value.id,
      {
        source: 'manual',
        evidence: {
          reason: 'inbox_listing_context_rejected',
          ui_surface: 'conversation_sidebar',
        },
      }
    );
    await updateFromResult(response);
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.ALERTS.REJECTED'));
  } catch {
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.ALERTS.REJECT_ERROR'));
  } finally {
    saving.value = false;
  }
};

const saveDemandProfile = async () => {
  savingDemand.value = true;

  try {
    const { data } = await ConversationBuyerProfilesAPI.update(
      props.conversationId,
      demandPayload()
    );
    applyDemandProfile(data?.data || data?.payload || null);
    editingDemand.value = false;
    await loadLinks();
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.SAVED'));
  } catch {
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.ERROR'));
  } finally {
    savingDemand.value = false;
  }
};

const demandActionRequest = action => {
  if (action === 'pause') return PropertyDemandsAPI.pause(activeDemandId.value);
  if (action === 'resume')
    return PropertyDemandsAPI.resume(activeDemandId.value);
  if (action === 'supersede') {
    return PropertyDemandsAPI.supersede(activeDemandId.value, {
      reason: 'conversation_sidebar_supersede',
    });
  }
  if (action === 'recompute_matches') {
    return PropertyDemandsAPI.recomputeMatches(activeDemandId.value);
  }
  return Promise.reject(new Error(`unsupported demand action: ${action}`));
};

const runDemandAction = async action => {
  if (!activeDemandId.value || savingDemandAction.value) return;

  savingDemandAction.value = action;

  try {
    await demandActionRequest(action);
    await loadLinks();
    useAlert(demandActionSuccessAlert(action));
  } catch {
    useAlert(t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ALERTS.ACTION_ERROR'));
  } finally {
    savingDemandAction.value = '';
  }
};

watch(
  () => props.conversationId,
  () => {
    selectedProperty.value = null;
    selectedSource.value = '';
    searchQuery.value = '';
    demandContext.value = null;
    loadLinks();
  }
);

onMounted(loadLinks);
</script>

<template>
  <section class="grid gap-3 px-3 py-4 text-[13px] text-n-slate-12">
    <div
      v-if="loading"
      class="rounded-lg border border-n-weak bg-n-slate-1 p-3 text-n-slate-11"
    >
      {{ t('CONVERSATION.LISTING_CONTEXT_CARD.LOADING') }}
    </div>

    <div v-else-if="error" class="grid gap-2">
      <p class="rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700">
        {{ error }}
      </p>
      <button
        type="button"
        class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-n-weak bg-white px-3 font-medium text-n-slate-12 hover:bg-n-slate-2"
        @click="loadLinks"
      >
        <span class="i-lucide-refresh-cw size-4" aria-hidden="true" />
        {{ t('CONVERSATION.LISTING_CONTEXT_CARD.RETRY') }}
      </button>
    </div>

    <template v-else>
      <article
        v-if="activeLink"
        class="grid gap-3 rounded-lg border border-n-weak bg-white p-3"
        data-testid="listing-context-active"
      >
        <div class="flex min-w-0 items-start justify-between gap-3">
          <div class="min-w-0">
            <p class="break-words font-semibold leading-snug">
              {{ titleFor(activeLink) }}
            </p>
            <p
              v-if="locationFor(activeLink)"
              class="mt-0.5 break-words text-[12px] text-n-slate-11"
            >
              {{ locationFor(activeLink) }}
            </p>
          </div>
          <span
            class="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
          >
            {{ stateLabel(activeLink.state) }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <div class="rounded-md bg-n-slate-1 px-2 py-1.5">
            <span class="block text-[11px] text-n-slate-11">
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.LABELS.MODALITY') }}
            </span>
            <span class="font-semibold">
              {{
                modalidadeFor(activeLink) ||
                t('CONVERSATION.LISTING_CONTEXT_CARD.NOT_INFORMED')
              }}
            </span>
          </div>
          <div class="rounded-md bg-n-slate-1 px-2 py-1.5">
            <span class="block text-[11px] text-n-slate-11">
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.LABELS.PRICE') }}
            </span>
            <span class="font-semibold">
              {{
                formatMoney(activeLink.listing?.price_amount) ||
                t('CONVERSATION.LISTING_CONTEXT_CARD.NOT_INFORMED')
              }}
            </span>
          </div>
          <div class="rounded-md bg-n-slate-1 px-2 py-1.5">
            <span class="block text-[11px] text-n-slate-11">
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.LABELS.AVAILABILITY') }}
            </span>
            <span class="font-semibold">
              {{
                availabilityLabel(activeLink.listing) ||
                t('CONVERSATION.LISTING_CONTEXT_CARD.NOT_INFORMED')
              }}
            </span>
          </div>
          <div class="rounded-md bg-n-slate-1 px-2 py-1.5">
            <span class="block text-[11px] text-n-slate-11">
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.LABELS.VERSION') }}
            </span>
            <span class="font-semibold">{{
              versionLabel(activeLink.listing)
            }}</span>
          </div>
        </div>

        <section
          class="grid gap-2 rounded-md border border-sky-100 bg-sky-50 px-3 py-2"
          data-testid="listing-context-demand"
        >
          <div class="flex items-center justify-between gap-2">
            <p class="text-[11px] font-semibold uppercase text-sky-800">
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.TITLE') }}
            </p>
            <button
              v-if="!editingDemand"
              type="button"
              data-testid="edit-demand-profile"
              class="inline-flex h-7 items-center justify-center gap-1 rounded-md border border-sky-200 bg-white px-2 text-[11px] font-semibold text-sky-800 hover:bg-sky-100"
              @click="startDemandEdit"
            >
              <span class="i-lucide-pencil size-3.5" aria-hidden="true" />
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ACTIONS.EDIT') }}
            </button>
          </div>
          <dl
            v-if="demandRows.length"
            class="grid gap-1 text-[12px] text-sky-950"
          >
            <div
              v-for="row in demandRows"
              :key="row.key"
              class="flex items-start justify-between gap-3"
            >
              <dt class="shrink-0 text-sky-700">{{ row.label }}</dt>
              <dd class="min-w-0 break-words text-right font-medium">
                {{ row.value }}
              </dd>
            </div>
          </dl>
          <p v-else class="text-[12px] text-sky-900">
            {{ t('CONVERSATION.LISTING_CONTEXT_CARD.NOT_INFORMED') }}
          </p>

          <div
            v-if="demandLifecycleActions.length"
            class="flex flex-wrap items-center gap-2 border-t border-sky-100 pt-2"
            data-testid="demand-lifecycle-actions"
          >
            <button
              v-for="action in demandLifecycleActions"
              :key="action.key"
              type="button"
              :data-testid="`demand-action-${action.key}`"
              class="inline-flex h-7 items-center justify-center gap-1 rounded-md border border-sky-200 bg-white px-2 text-[11px] font-semibold text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="!!savingDemandAction || savingDemand"
              @click="runDemandAction(action.key)"
            >
              <span
                :class="
                  savingDemandAction === action.key
                    ? 'i-lucide-loader-circle animate-spin'
                    : action.icon
                "
                class="size-3.5"
                aria-hidden="true"
              />
              {{ demandActionLabel(action.key) }}
            </button>
          </div>

          <form
            v-if="editingDemand"
            class="grid gap-2 border-t border-sky-100 pt-2"
            data-testid="demand-profile-form"
            @submit.prevent="saveDemandProfile"
          >
            <div class="grid grid-cols-2 gap-2">
              <label class="grid gap-1 text-[11px] font-medium text-sky-900">
                {{
                  t(
                    'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.FINALIDADE'
                  )
                }}
                <select
                  v-model="demandForm.finalidade"
                  data-testid="demand-finalidade"
                  class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
                >
                  <option value="">
                    {{ t('CONVERSATION.LISTING_CONTEXT_CARD.NOT_INFORMED') }}
                  </option>
                  <option value="venda">
                    {{ t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.SALE') }}
                  </option>
                  <option value="aluguel">
                    {{ t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.RENT') }}
                  </option>
                  <option value="temporada">
                    {{
                      t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITIES.SEASONAL')
                    }}
                  </option>
                </select>
              </label>

              <label class="grid gap-1 text-[11px] font-medium text-sky-900">
                {{
                  t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.PAYMENT')
                }}
                <select
                  v-model="demandForm.payment_kind"
                  data-testid="demand-payment-kind"
                  class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
                >
                  <option value="">
                    {{ t('CONVERSATION.LISTING_CONTEXT_CARD.NOT_INFORMED') }}
                  </option>
                  <option value="a_vista">
                    {{
                      t(
                        'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.CASH'
                      )
                    }}
                  </option>
                  <option value="financiamento">
                    {{
                      t(
                        'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.FINANCING'
                      )
                    }}
                  </option>
                  <option value="a_vista_fgts">
                    {{
                      t(
                        'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.CASH_FGTS'
                      )
                    }}
                  </option>
                  <option value="financiamento_fgts">
                    {{
                      t(
                        'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.PAYMENTS.FINANCING_FGTS'
                      )
                    }}
                  </option>
                </select>
              </label>

              <label class="grid gap-1 text-[11px] font-medium text-sky-900">
                {{
                  t(
                    'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.FIELDS.BUDGET_MIN'
                  )
                }}
                <input
                  v-model="demandForm.budget_min"
                  data-testid="demand-budget-min"
                  type="number"
                  min="0"
                  class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
                />
              </label>

              <label class="grid gap-1 text-[11px] font-medium text-sky-900">
                {{
                  t(
                    'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.FIELDS.BUDGET_MAX'
                  )
                }}
                <input
                  v-model="demandForm.budget_max"
                  data-testid="demand-budget-max"
                  type="number"
                  min="0"
                  class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
                />
              </label>
            </div>

            <label class="grid gap-1 text-[11px] font-medium text-sky-900">
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.REGIONS') }}
              <input
                v-model="demandForm.regions"
                data-testid="demand-regions"
                class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
              />
            </label>

            <label class="grid gap-1 text-[11px] font-medium text-sky-900">
              {{
                t(
                  'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.PROPERTY_TYPES'
                )
              }}
              <input
                v-model="demandForm.property_types"
                data-testid="demand-property-types"
                class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
              />
            </label>

            <div class="grid grid-cols-2 gap-2">
              <label class="grid gap-1 text-[11px] font-medium text-sky-900">
                {{
                  t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.BEDROOMS')
                }}
                <input
                  v-model="demandForm.bedrooms"
                  data-testid="demand-bedrooms"
                  class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
                />
              </label>

              <label class="grid gap-1 text-[11px] font-medium text-sky-900">
                {{
                  t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.LABELS.INTENT')
                }}
                <select
                  v-model="demandForm.intent"
                  data-testid="demand-intent"
                  class="h-8 rounded-md border border-sky-200 bg-white px-2 text-[12px]"
                >
                  <option value="">
                    {{ t('CONVERSATION.LISTING_CONTEXT_CARD.NOT_INFORMED') }}
                  </option>
                  <option value="home">
                    {{
                      t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.INTENTS.HOME')
                    }}
                  </option>
                  <option value="investor">
                    {{
                      t(
                        'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.INTENTS.INVESTOR'
                      )
                    }}
                  </option>
                </select>
              </label>
            </div>

            <label
              class="flex items-center gap-2 text-[12px] font-medium text-sky-900"
            >
              <input
                v-model="demandForm.first_purchase"
                data-testid="demand-first-purchase"
                type="checkbox"
                class="size-4 rounded border-sky-300"
              />
              {{
                t(
                  'CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.FIELDS.FIRST_PURCHASE'
                )
              }}
            </label>

            <div class="flex items-center justify-end gap-2">
              <button
                type="button"
                data-testid="cancel-demand-profile"
                class="inline-flex h-8 items-center justify-center rounded-md border border-sky-200 bg-white px-3 text-[12px] font-semibold text-sky-800 hover:bg-sky-100"
                :disabled="savingDemand"
                @click="cancelDemandEdit"
              >
                {{
                  t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ACTIONS.CANCEL')
                }}
              </button>
              <button
                type="submit"
                data-testid="save-demand-profile"
                class="inline-flex h-8 items-center justify-center gap-1 rounded-md bg-sky-700 px-3 text-[12px] font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
                :disabled="savingDemand"
              >
                <span
                  :class="
                    savingDemand
                      ? 'i-lucide-loader-circle animate-spin'
                      : 'i-lucide-check'
                  "
                  class="size-3.5"
                  aria-hidden="true"
                />
                {{ t('CONVERSATION.LISTING_CONTEXT_CARD.DEMAND.ACTIONS.SAVE') }}
              </button>
            </div>
          </form>
        </section>

        <section
          v-if="activityRows.length || taskRows.length"
          class="grid gap-2 rounded-md border border-violet-100 bg-violet-50 px-3 py-2"
          data-testid="listing-context-activity"
        >
          <p class="text-[11px] font-semibold uppercase text-violet-800">
            {{ t('CONVERSATION.LISTING_CONTEXT_CARD.ACTIVITY.TITLE') }}
          </p>
          <dl
            v-if="activityRows.length"
            class="grid gap-1 text-[12px] text-violet-950"
          >
            <div
              v-for="row in activityRows"
              :key="row.key"
              class="flex items-start justify-between gap-3"
            >
              <dt class="shrink-0 text-violet-700">{{ row.label }}</dt>
              <dd class="min-w-0 break-words text-right font-medium">
                {{ row.value }}
              </dd>
            </div>
          </dl>
          <div v-if="taskRows.length" class="grid gap-1">
            <div
              v-for="task in taskRows"
              :key="task.key"
              class="flex items-center justify-between gap-2 rounded bg-white/70 px-2 py-1 text-[12px]"
            >
              <span class="min-w-0 break-words font-medium text-violet-950">
                {{ task.label }}
              </span>
              <span
                class="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                :class="
                  task.status === 'done'
                    ? 'bg-emerald-50 text-emerald-700'
                    : 'bg-amber-50 text-amber-700'
                "
              >
                {{ task.statusLabel }}
              </span>
            </div>
          </div>
        </section>

        <button
          type="button"
          data-testid="reject-listing-context"
          class="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-rose-200 bg-white px-3 font-medium text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="saving"
          @click="rejectActiveLink"
        >
          <span class="i-lucide-x-circle size-4" aria-hidden="true" />
          {{ t('CONVERSATION.LISTING_CONTEXT_CARD.REJECT') }}
        </button>
      </article>

      <div
        v-else
        class="rounded-lg border border-dashed border-n-weak bg-n-slate-1 p-3 text-n-slate-11"
        data-testid="listing-context-empty"
      >
        {{ t('CONVERSATION.LISTING_CONTEXT_CARD.EMPTY') }}
      </div>

      <div class="grid gap-2">
        <LaisPropertySearch
          v-model="selectedProperty"
          v-model:source="selectedSource"
          v-model:query="searchQuery"
          :label="t('CONVERSATION.LISTING_CONTEXT_CARD.SEARCH_LABEL')"
          helper=""
          :placeholder="
            t('CONVERSATION.LISTING_CONTEXT_CARD.SEARCH_PLACEHOLDER')
          "
          :available-only="false"
          :per-source="4"
          floating-results
          treat-properties-as-portfolio
          @select="confirmSelection"
        />

        <p
          v-if="selectedMissingListing"
          class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800"
        >
          {{ t('CONVERSATION.LISTING_CONTEXT_CARD.MISSING_LISTING') }}
        </p>

        <button
          type="button"
          class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-n-brand px-3 font-medium text-white hover:bg-n-brand/90 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="!canConfirmSelection"
          @click="confirmSelection"
        >
          <span
            :class="
              saving ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-check'
            "
            class="size-4"
            aria-hidden="true"
          />
          {{ t('CONVERSATION.LISTING_CONTEXT_CARD.CONFIRM') }}
        </button>
      </div>

      <div v-if="historyLinks.length" class="grid gap-2">
        <p class="text-[11px] font-semibold uppercase text-n-slate-11">
          {{ t('CONVERSATION.LISTING_CONTEXT_CARD.RECENT_HISTORY') }}
        </p>
        <article
          v-for="link in historyLinks"
          :key="link.id"
          class="rounded-md border border-n-weak px-3 py-2"
        >
          <div class="flex items-center justify-between gap-2">
            <p class="min-w-0 truncate font-medium">{{ titleFor(link) }}</p>
            <span class="shrink-0 text-[11px] text-n-slate-11">
              {{ stateLabel(link.state) }}
            </span>
          </div>
          <p class="mt-0.5 text-[12px] text-n-slate-11">
            {{
              modalidadeFor(link) ||
              t('CONVERSATION.LISTING_CONTEXT_CARD.MODALITY_NOT_INFORMED')
            }}
            <span v-if="link.listing?.listing_version">
              {{ t('CONVERSATION.LISTING_CONTEXT_CARD.HISTORY_SEPARATOR')
              }}{{ versionLabel(link.listing) }}
            </span>
          </p>
        </article>
      </div>
    </template>
  </section>
</template>
